
import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import './Doctors.css';
import { FaEdit, FaPlus, FaTrash, FaCopy } from 'react-icons/fa';
import useAuth from '../hooks/useAuth';
import { canDo } from '../utils/canDo';
import CustomDataTable from './CustomDataTable';

const Exercises = function () {
  var auth = useAuth().auth;

  var [exercises, setExercises] = useState([]);
  var [dtypes, setDTypes] = useState([]);
  var [bpConfig, setBpConfig] = useState({ front: [], back: [] });
  var [isEditMode, setIsEditMode] = useState(true);
  var [newExercise, setNewExercise] = useState({
    name: '',
    repetition: 1,
    duration: 1,
    remarks: '',
    stype: [],
    bodyParts: [],
    vlink: ''
  });
  var [selectedExercise, setSelectedExercise] = useState(null);
  var [isModalOpen, setIsModalOpen] = useState(false);
  var [bpView, setBpView] = useState('front'); // 'front' | 'back'

  useEffect(function () {
    getDTypes();
    getExercises();
    getBPConfig();
  }, []);

  function getBPConfig() {
    api.get(process.env.REACT_APP_URL + '/bodyparts')
      .then(function (response) {
        setBpConfig({
          front: response.data?.front || [],
          back: response.data?.back || [],
        });
      })
      .catch(function (error) {
        console.error('Error fetching BPConfig:', error);
      });
  }

  function getDTypes() {
    api.get(process.env.REACT_APP_URL + '/sequences/dtypes')
      .then(function (response) {
        var exers = Array.isArray(response.data) ? response.data : [];
        setDTypes(exers);
      })
      .catch(function (error) {
        console.error('Error fetching Exercise types:', error);
        setDTypes([]);
      });
  }

  function getExercises() {
    api.get(process.env.REACT_APP_URL + '/sequences')
      .then(function (response) {
        var exers = Array.isArray(response.data) ? response.data : [];
        setExercises(exers);
      })
      .catch(function (error) {
        console.error('Error fetching Exercises:', error);
        setExercises([]);
      });
  }

  // Build a map: body part key → stype for auto-deriving stype from selected body parts
  const bpStypeMap = useMemo(() => {
    const map = {};
    [...(bpConfig.front || []), ...(bpConfig.back || [])].forEach(p => {
      if (p.key && p.stype) map[p.key] = p.stype;
    });
    return map;
  }, [bpConfig]);

  // Build a map: body part key → label
  const bpLabelMap = useMemo(() => {
    const map = {};
    [...(bpConfig.front || []), ...(bpConfig.back || [])].forEach(p => {
      if (p.key) map[p.key] = p.label || p.key;
    });
    return map;
  }, [bpConfig]);

  // Derive unique stypes from selected body parts
  function deriveStypes(bodyPartKeys) {
    const stypes = new Set();
    (bodyPartKeys || []).forEach(key => {
      if (bpStypeMap[key]) stypes.add(bpStypeMap[key]);
    });
    return Array.from(stypes);
  }

  var columns = [
    {
      name: "Name",
      sortable: true,
      selector: function (row) { return row?.name || ""; }
    },
    {
      name: "Body Parts",
      sortable: false,
      cell: function (row) {
        const parts = row?.bodyParts || [];
        if (parts.length === 0) {
          // Fallback: show stype for old exercises
          return (row?.stype || []).join(', ');
        }
        return parts.map(k => bpLabelMap[k] || k).join(', ');
      },
      wrap: true,
    },
    {
      name: "Used In (stype)",
      sortable: true,
      selector: function (row) {
        if (!row || !row.stype) return "";
        return Array.isArray(row.stype) ? row.stype.join(", ") : (typeof row.stype === 'string' ? row.stype : "");
      }
    },
    {
      name: "Reps",
      selector: function (row) { return row?.repetition || 1; },
      sortable: true,
      width: '70px',
    },
    {
      name: "Dur (min)",
      selector: function (row) { return row?.duration || 1; },
      sortable: true,
      width: '80px',
    },
    {
      name: "Video",
      selector: function (row) { return row?.vlink || ""; },
      width: '100px',
    },
    {
      name: "Remarks",
      selector: function (row) { return row?.remarks || ""; }
    },
    {
      name: "Actions",
      sortable: false,
      omit: !canDo(auth, 'exercises.edit') && !canDo(auth, 'exercises.add') && !canDo(auth, 'exercises.delete'),
      width: '100px',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {canDo(auth, 'exercises.add') && <FaCopy onClick={() => duplicateExer(row._id)} size={16} style={{ cursor: 'pointer', color: '#6366f1' }} title="Duplicate" />}
          {canDo(auth, 'exercises.edit') && <FaEdit onClick={() => updateExer(row._id)} size={16} style={{ cursor: 'pointer', color: 'green' }} title="Edit" />}
          {canDo(auth, 'exercises.delete') && <FaTrash onClick={() => handleDeleteExercise(row._id)} size={16} style={{ cursor: 'pointer', color: 'red' }} title="Delete" />}
        </div>
      )
    }
  ];

  function handleAddExer(e) {
    e.preventDefault();
    const trimmedName = (newExercise.name || '').trim();
    if (!trimmedName) { alert('Exercise name is required.'); return; }
    if (!window.confirm(`Create exercise with name:\n\n"${trimmedName}"\n\nThis name cannot be changed later. Continue?`)) return;
    const dataToSubmit = {
      ...newExercise,
      name: trimmedName,
      repetition: parseInt(newExercise.repetition, 10) || 1,
      duration: parseInt(newExercise.duration, 10) || 1,
      stype: deriveStypes(newExercise.bodyParts),
    };
    api.post(process.env.REACT_APP_URL + "/sequences/add", dataToSubmit)
      .then(function () {
        setNewExercise({ name: '', repetition: 1, duration: 1, remarks: '', stype: [], bodyParts: [], vlink: '' });
        handleClose();
      })
      .catch(function (err) { window.alert(err?.response?.data?.error || "Error adding exercise"); });
  }

  function handleUpdateExer(e) {
    e.preventDefault();
    if (!selectedExercise) { alert("No exercise selected."); return; }
    const dataToSubmit = {
      ...selectedExercise,
      stype: deriveStypes(selectedExercise.bodyParts),
    };
    api.post(process.env.REACT_APP_URL + "/sequences/update/" + selectedExercise._id, dataToSubmit)
      .then(function () { handleClose(); })
      .catch(function () { alert("Error updating exercise."); });
  }

  function handleClose() {
    setIsModalOpen(false);
    setIsEditMode(false);
    if (selectedExercise) setSelectedExercise(null);
    getExercises();
  }

  function addNewExers(e) {
    e.preventDefault();
    setSelectedExercise(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  }

  function updateExer(id) {
    var p = exercises.find(function (ex) { return ex._id === id; });
    setSelectedExercise({ ...p, bodyParts: p?.bodyParts || [] });
    setIsModalOpen(true);
    setIsEditMode(true);
  }

  function duplicateExer(id) {
    var p = exercises.find(function (ex) { return ex._id === id; });
    if (!p) return;
    // Pre-fill the new exercise form with the selected exercise's details (no _id)
    setNewExercise({
      name: p.name + ' (copy)',
      repetition: p.repetition || 1,
      duration: p.duration || 1,
      remarks: p.remarks || '',
      vlink: p.vlink || '',
      bodyParts: [...(p.bodyParts || [])],
      stype: p.stype || [],
    });
    setSelectedExercise(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  }

  function handleDeleteExercise(id) {
    if (!window.confirm("Are you sure you want to delete this?")) return;
    api.delete(process.env.REACT_APP_URL + "/sequences/delete/" + id)
      .then(function () { setSelectedExercise(null); handleClose(); })
      .catch(function (error) { console.error("Error deleting exercise:", error); });
  }

  // Toggle body part checkbox
  function toggleBodyPart(key) {
    const current = isEditMode ? (selectedExercise?.bodyParts || []) : (newExercise.bodyParts || []);
    const updated = current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key];

    if (isEditMode) {
      setSelectedExercise(prev => ({ ...prev, bodyParts: updated }));
    } else {
      setNewExercise(prev => ({ ...prev, bodyParts: updated }));
    }
  }

  const currentBodyParts = isEditMode ? (selectedExercise?.bodyParts || []) : (newExercise.bodyParts || []);
  const partsForView = bpView === 'front' ? (bpConfig.front || []) : (bpConfig.back || []);

  // All parts combined for the selected-chips sidebar
  const allParts = useMemo(() => [...(bpConfig.front || []), ...(bpConfig.back || [])], [bpConfig]);

  // SVG body map constants (same as BPSelection)
  const SVG_W = 390, SVG_H = 520;

  // Tooltip state for body map hover
  const [hoveredPart, setHoveredPart] = useState(null);

  // ── Styles ──
  const S = {
    formRow: { marginBottom: 12 },
    label: { display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14, color: '#333' },
    input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' },
    viewToggle: { display: 'flex', gap: 0, marginBottom: 10, borderRadius: 8, overflow: 'hidden', border: '2px solid #4f46e5' },
    viewBtn: (active) => ({
      flex: 1, padding: '7px 0', textAlign: 'center', fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none',
      background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#4f46e5', transition: 'all 0.15s',
    }),
    selectedChip: {
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600,
      cursor: 'pointer', margin: '2px 3px 2px 0', background: '#4f46e5', color: '#fff', border: 'none',
    },
    selectedCount: { fontSize: 12, color: '#6b7280', marginBottom: 6 },
    submitBtn: {
      marginTop: 16, padding: '10px 20px', backgroundColor: '#4f46e5', color: 'white', border: 'none',
      borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer',
    },
  };

  if (isModalOpen) {
    const exObj = isEditMode ? selectedExercise : newExercise;
    const setField = (key, val) => {
      if (isEditMode) setSelectedExercise(prev => ({ ...prev, [key]: val }));
      else setNewExercise(prev => ({ ...prev, [key]: val }));
    };

    return (
      <div style={{ padding: 16, maxWidth: 800, margin: '0 auto', height: '100%', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 style={{ margin: 0 }}>{isEditMode ? "Edit Exercise" : "Add New Exercise"}</h4>
          <button onClick={handleClose} style={{ padding: '6px 16px', borderRadius: 6, border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 600 }}>Close</button>
        </div>

        <form onSubmit={isEditMode ? handleUpdateExer : handleAddExer}>
          {/* Duplicate from existing — only in Add mode */}
          {!isEditMode && exercises.length > 0 && (
            <div style={{ ...S.formRow, marginBottom: 14 }}>
              <label style={{ ...S.label, color: '#6366f1' }}>Copy from existing</label>
              <select style={S.input} value="" onChange={e => { if (e.target.value) duplicateExer(e.target.value); }}>
                <option value="">— Select to duplicate —</option>
                {exercises.map(ex => <option key={ex._id} value={ex._id}>{ex.name}</option>)}
              </select>
            </div>
          )}
          {/* Name */}
          <div style={S.formRow}>
            <label style={S.label}>Name {isEditMode && <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>(cannot be changed)</span>}</label>
            <input style={{ ...S.input, ...(isEditMode ? { background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' } : {}) }} type="text" value={exObj?.name || ''} onChange={e => setField('name', e.target.value)} readOnly={isEditMode} required />
          </div>

          {/* Reps & Duration row */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Repetition</label>
              <input style={S.input} type="number" min="1" value={exObj?.repetition ?? 1} onChange={e => setField('repetition', parseInt(e.target.value, 10) || '')} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Duration (min)</label>
              <input style={S.input} type="number" min="1" value={exObj?.duration ?? 1} onChange={e => setField('duration', parseInt(e.target.value, 10) || '')} />
            </div>
          </div>

          {/* Video Link */}
          <div style={S.formRow}>
            <label style={S.label}>Video Link</label>
            <input style={S.input} type="text" value={exObj?.vlink || ''} onChange={e => setField('vlink', e.target.value)} />
          </div>

          {/* Remarks */}
          <div style={S.formRow}>
            <label style={S.label}>Remarks</label>
            <input style={S.input} type="text" value={exObj?.remarks || ''} onChange={e => setField('remarks', e.target.value)} />
          </div>

          {/* ── Body Parts — Visual Body Map ── */}
          <div style={{ marginTop: 16, padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <label style={{ ...S.label, fontSize: 15, marginBottom: 6 }}>Body Parts — tap on the body to select</label>
            <div style={S.selectedCount}>
              {currentBodyParts.length} part{currentBodyParts.length !== 1 ? 's' : ''} selected
              {currentBodyParts.length > 0 && (
                <span> — stype: {deriveStypes(currentBodyParts).join(', ')}</span>
              )}
            </div>

            {/* Front / Back toggle */}
            <div style={S.viewToggle}>
              <button type="button" style={S.viewBtn(bpView === 'front')} onClick={() => setBpView('front')}>
                Front ({(bpConfig.front || []).length})
              </button>
              <button type="button" style={S.viewBtn(bpView === 'back')} onClick={() => setBpView('back')}>
                Back ({(bpConfig.back || []).length})
              </button>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              {/* SVG Body Map */}
              <div style={{ flex: '0 0 auto', position: 'relative' }}>
                <svg
                  viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                  style={{ display: 'block', width: 495, height: 825, borderRadius: 8, background: '#fff', border: '1px solid #e2e8f0' }}
                >
                  <image href={`/${bpView}_body.png?v=2`} x={0} y={0} width={SVG_W} height={SVG_H} preserveAspectRatio="none" />
                  {partsForView.map(p => {
                    const checked = currentBodyParts.includes(p.key);
                    const isHov = hoveredPart === p.key;
                    if (p.type === 'line') {
                      const midX = ((p.x1 || 0) + (p.x2 || 0)) / 2;
                      const midY = ((p.y1 || 0) + (p.y2 || 0)) / 2;
                      return (
                        <g key={p.key} style={{ cursor: 'pointer' }}
                          onClick={() => toggleBodyPart(p.key)}
                          onMouseEnter={() => setHoveredPart(p.key)}
                          onMouseLeave={() => setHoveredPart(null)}
                        >
                          <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2}
                            stroke="transparent" strokeWidth={16} />
                          <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2}
                            stroke={checked ? '#4f46e5' : isHov ? '#818cf8' : '#ef4444'}
                            strokeWidth={checked ? 3.5 : 2.5} strokeLinecap="round"
                            opacity={checked ? 1 : 0.7} />
                          {isHov && (
                            <text x={midX} y={midY - 8} textAnchor="middle"
                              fontSize="9" fontWeight="700"
                              fill={checked ? '#4f46e5' : '#1e293b'}
                              stroke="#fff" strokeWidth="3" paintOrder="stroke"
                              style={{ pointerEvents: 'none' }}>
                              {p.label}
                            </text>
                          )}
                        </g>
                      );
                    }
                    // Dot type
                    const cx = p.cx || 0, cy = p.cy || 0, r = p.r || 7;
                    return (
                      <g key={p.key} style={{ cursor: 'pointer' }}
                        onClick={() => toggleBodyPart(p.key)}
                        onMouseEnter={() => setHoveredPart(p.key)}
                        onMouseLeave={() => setHoveredPart(null)}
                      >
                        {/* Larger invisible hit area */}
                        <circle cx={cx} cy={cy} r={Math.max(r, 10)} fill="transparent" />
                        {/* Visible dot */}
                        <circle cx={cx} cy={cy} r={r}
                          fill={checked ? 'rgba(79,70,229,0.6)' : isHov ? 'rgba(129,140,248,0.5)' : 'rgba(239,68,68,0.35)'}
                          stroke={checked ? '#4f46e5' : isHov ? '#818cf8' : '#dc2626'}
                          strokeWidth={checked ? 2 : 1.5}
                        />
                        {checked && (
                          <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="central"
                            fontSize="8" fontWeight="900" fill="#fff" style={{ pointerEvents: 'none' }}>
                            &#10003;
                          </text>
                        )}
                        {isHov && (
                          <text x={cx} y={cy + r + 10} textAnchor="middle"
                            fontSize="8" fontWeight="700"
                            fill={checked ? '#4f46e5' : '#1e293b'}
                            stroke="#fff" strokeWidth="3" paintOrder="stroke"
                            style={{ pointerEvents: 'none' }}>
                            {p.label}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </svg>
                {partsForView.length === 0 && (
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#9ca3af', fontSize: 11, textAlign: 'center', width: '80%' }}>
                    No body parts configured. Go to BPConfig to set up.
                  </div>
                )}
              </div>

              {/* Selected parts sidebar */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                  Selected ({currentBodyParts.length})
                </div>
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {currentBodyParts.length === 0 && (
                    <div style={{ color: '#9ca3af', fontSize: 12, padding: '8px 0' }}>
                      Tap body parts on the map to select them
                    </div>
                  )}
                  {currentBodyParts.map(key => {
                    const part = allParts.find(p => p.key === key);
                    const label = part?.label || bpLabelMap[key] || key;
                    return (
                      <span key={key} style={S.selectedChip} onClick={() => toggleBodyPart(key)} title="Click to remove">
                        {label} &times;
                      </span>
                    );
                  })}
                </div>
                {currentBodyParts.length > 0 && (
                  <button type="button"
                    onClick={() => {
                      if (isEditMode) setSelectedExercise(prev => ({ ...prev, bodyParts: [] }));
                      else setNewExercise(prev => ({ ...prev, bodyParts: [] }));
                    }}
                    style={{ marginTop: 8, fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>

          <button type="submit" style={S.submitBtn}>
            {isEditMode ? "Update Exercise" : "Add Exercise"}
          </button>
        </form>
      </div>
    );
  }

  // ── List View ──
  return (
    <div>
      <div className="tableheaderActions">
        <button id="cnew" type="button" className="btn btn-outline-primary" onClick={addNewExers}>Add New</button>
      </div>
      {exercises && exercises.length > 0 &&
        <CustomDataTable
          columns={columns}
          data={exercises}
          paginationPerPage={100}
          selectableRows={false}
        />
      }
    </div>
  );
};

export default Exercises;
